/**
 * DEMO-ONLY: sessionStorage-backed ICM product stock/price notifications.
 * Supports any catalog sku (sku === manufacturerSKU) for back-in-stock alerts.
 */
import { getPresidioMockActiveLogin, PRESIDIO_MOCK_DEFAULT_LOGIN } from './presidio-mock-session';

export const PRESIDIO_MOCK_PRODUCT_NOTIFICATIONS_KEY = 'presidio-mock-product-notifications';

/** Session flag — seed once per browser session (survives navigate; cleared with reset). */
export const PRESIDIO_MOCK_PRODUCT_NOTIFICATIONS_SEEDED_KEY = 'presidio-mock-product-notifications-seeded';

export type PresidioMockProductNotificationType = 'stock' | 'price';

export interface PresidioMockProductNotification {
  sku: string;
  notificationMailAddress: string;
  type: PresidioMockProductNotificationType;
  price?: { type: 'Money'; value: number; currency: string };
}

type StoreShape = Record<string, PresidioMockProductNotification[]>;

/** Catalog SKUs that exist in LOCAL_MOCK product fixtures — demo back-in-stock watch list. */
const DEMO_STOCK_SKUS = ['BRIO-4K', 'Q1', '1250g', 'CISCO-C9300', 'IPHONE15P-256'] as const;

function readStore(): StoreShape {
  if (typeof sessionStorage === 'undefined') {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_MOCK_PRODUCT_NOTIFICATIONS_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as StoreShape;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store: StoreShape): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_PRODUCT_NOTIFICATIONS_KEY, JSON.stringify(store));
}

function userKey(login?: string | null): string {
  return ((login ?? getPresidioMockActiveLogin()) || 'anonymous').trim().toLowerCase() || 'anonymous';
}

/**
 * Seed back-in-stock alerts for the active (and default) demo user once per session.
 * Idempotent — does not overwrite rows the user already created/deleted for a SKU after seed.
 */
export function ensurePresidioMockStockNotificationSeed(login?: string | null): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  if (sessionStorage.getItem(PRESIDIO_MOCK_PRODUCT_NOTIFICATIONS_SEEDED_KEY) === '1') {
    return;
  }

  const logins = new Set<string>([userKey(login), userKey(PRESIDIO_MOCK_DEFAULT_LOGIN)]);
  const store = readStore();

  for (const key of logins) {
    const existing = store[key] || [];
    const haveStockSku = new Set(existing.filter(n => n.type === 'stock').map(n => n.sku));
    const email = key.includes('@') ? key : `${key}@example.com`;
    const additions: PresidioMockProductNotification[] = DEMO_STOCK_SKUS.filter(sku => !haveStockSku.has(sku)).map(
      sku => ({
        sku,
        type: 'stock' as const,
        notificationMailAddress: email,
      })
    );
    if (additions.length) {
      store[key] = [...existing, ...additions];
    }
  }

  writeStore(store);
  sessionStorage.setItem(PRESIDIO_MOCK_PRODUCT_NOTIFICATIONS_SEEDED_KEY, '1');
}

function listForUser(
  type: PresidioMockProductNotificationType,
  login?: string | null
): PresidioMockProductNotification[] {
  if (type === 'stock') {
    ensurePresidioMockStockNotificationSeed(login);
  }
  const store = readStore();
  return (store[userKey(login)] || []).filter(n => n.type === type);
}

function upsert(
  notification: PresidioMockProductNotification,
  login?: string | null
): PresidioMockProductNotification {
  const key = userKey(login);
  const store = readStore();
  const existing = store[key] || [];
  const next = existing.filter(n => !(n.type === notification.type && n.sku === notification.sku));
  next.push(notification);
  store[key] = next;
  writeStore(store);
  return notification;
}

function remove(type: PresidioMockProductNotificationType, sku: string, login?: string | null): void {
  const key = userKey(login);
  const store = readStore();
  store[key] = (store[key] || []).filter(n => !(n.type === type && n.sku === sku));
  writeStore(store);
}

function notificationUri(customerNo: string, type: PresidioMockProductNotificationType, sku: string): string {
  return `inSPIRED-inTRONICS_Business-Site/-/customers/${customerNo}/users/-/notifications/${type}/${encodeURIComponent(
    sku
  )}`;
}

export function listPresidioMockProductNotificationLinks(
  customerNo: string,
  type: PresidioMockProductNotificationType
): { elements: Array<{ type: string; uri: string; title: string }> } {
  return {
    elements: listForUser(type).map(n => ({
      type: 'Link',
      uri: notificationUri(customerNo || 'PRESIDIO-MOCK', type, n.sku),
      title: n.sku,
    })),
  };
}

export function getPresidioMockProductNotification(
  type: PresidioMockProductNotificationType,
  sku: string
): PresidioMockProductNotification | undefined {
  const decoded = decodeURIComponent(sku);
  return listForUser(type).find(n => n.sku === decoded);
}

export function createPresidioMockProductNotification(
  customerNo: string,
  type: PresidioMockProductNotificationType,
  body: Partial<PresidioMockProductNotification> | null | undefined
): { type: string; uri: string; title: string } {
  const sku = (body?.sku || '').trim();
  if (!sku) {
    throw new Error('createPresidioMockProductNotification requires sku');
  }
  const saved = upsert({
    sku,
    type,
    notificationMailAddress: (body?.notificationMailAddress || getPresidioMockActiveLogin() || 'demo@example.com').trim(),
    price: body?.price,
  });
  return {
    type: 'Link',
    uri: notificationUri(customerNo || 'PRESIDIO-MOCK', type, saved.sku),
    title: saved.sku,
  };
}

export function updatePresidioMockProductNotification(
  type: PresidioMockProductNotificationType,
  sku: string,
  body: Partial<PresidioMockProductNotification> | null | undefined
): PresidioMockProductNotification {
  const decoded = decodeURIComponent(sku);
  const existing = getPresidioMockProductNotification(type, decoded);
  return upsert({
    sku: decoded,
    type,
    notificationMailAddress:
      body?.notificationMailAddress ||
      existing?.notificationMailAddress ||
      getPresidioMockActiveLogin() ||
      'demo@example.com',
    price: body?.price ?? existing?.price,
  });
}

export function deletePresidioMockProductNotification(
  type: PresidioMockProductNotificationType,
  sku: string
): void {
  remove(type, decodeURIComponent(sku));
}

export function resetPresidioMockProductNotifications(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(PRESIDIO_MOCK_PRODUCT_NOTIFICATIONS_KEY);
  sessionStorage.removeItem(PRESIDIO_MOCK_PRODUCT_NOTIFICATIONS_SEEDED_KEY);
}
